import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

const ACTIVITY_LOGS_URL = '/activitylogs/';

export interface LogUser {
    first_name: string;
    last_name: string;
    email: string;
}

export interface ActivityLog {
    id: number;
    user: LogUser | null;
    action: string;
    mensaje: string;
    fecha: string;
}

export interface ActivityLogFilters {
    usuario?: string;
    accion?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
    page?: number;
    page_size?: number;
}

export interface ActivityLogPage {
    results: ActivityLog[];
    count: number;
    paginated: boolean;
}

const cleanParams = (filters: ActivityLogFilters) =>
    Object.fromEntries(
        Object.entries(filters).flatMap(([key, rawValue]) => {
            const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
            return value === '' || value === undefined || value === null ? [] : [[key, value]];
        }),
    );

const isLogUser = (value: unknown): value is LogUser => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const user = value as Record<string, unknown>;
    return typeof user.first_name === 'string'
        && typeof user.last_name === 'string'
        && typeof user.email === 'string';
};

const isActivityLog = (value: unknown): value is ActivityLog => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const log = value as Record<string, unknown>;
    return Number.isInteger(log.id)
        && (log.user === null || isLogUser(log.user))
        && typeof log.action === 'string'
        && typeof log.mensaje === 'string'
        && typeof log.fecha === 'string'
        && Number.isFinite(Date.parse(log.fecha));
};

const normalizeResponse = (data: ActivityLog[] | { results?: ActivityLog[]; count?: number }): ActivityLogPage => {
    const invalid = () => new Error('Respuesta inválida del servicio de actividad.');
    if (Array.isArray(data)) {
        if (!data.every(isActivityLog)) throw invalid();
        return { results: data, count: data.length, paginated: false };
    }
    if (!data || !Array.isArray(data.results) || !data.results.every(isActivityLog)
        || !Number.isInteger(data.count) || (data.count as number) < 0) {
        throw invalid();
    }
    return { results: data.results, count: data.count as number, paginated: true };
};

export const getActivityLogs = async (filters: ActivityLogFilters = {}): Promise<ActivityLogPage> =>
    handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(ACTIVITY_LOGS_URL, { params: cleanParams(filters) });
        return normalizeResponse(response.data);
    });

